/**
 * Where a person lands after signing in: the page they were trying to open.
 *
 * The web app has one door, "eis ekosis ile Giriş Yap", and it used to forget where the
 * person was going: DashboardLayout sent a signed-out visitor to a bare /login, and
 * AuthCallback always landed on the role's home. That made every deep link (an LMS
 * notification, a link from the Öğrenci Paneli) end on the dashboard. This file carries the
 * destination across the sign-in round trip, and it is the ONLY place that decides what a
 * destination may be.
 *
 * ⚠️ A destination arrives from a URL anybody can craft (`/login?next=…`), so it is an open
 * redirect waiting to happen. The rules, all checked here and nowhere else:
 *   • a PATH of this app (same origin), never an address: parsed with `new URL(raw, origin)`
 *     and required to come out on that origin, not judged by a prefix check;
 *   • `/student/…`, `/teacher/…` or `/parent/…` — and, once the signed-in role is known, only
 *     THAT role's: a student cannot be sent into another role's pages;
 *   • no backslash and no control character anywhere in the text: browsers read `/\host` as
 *     `//host`, and the URL parser silently drops tab / CR / LF, so "/\t/evil.example" would
 *     become "//evil.example" after the check had passed;
 *   • not `//…`, and no `//` inside the path;
 *   • the PARSED path is what is judged and what is returned (dot segments and %2e resolved),
 *     so `/student/../teacher/x` is a /teacher path, not a /student one;
 *   • at most MAX_NEXT_LENGTH characters; the fragment is dropped.
 *
 * Across the round trip the destination waits in sessionStorage (the backend's login door has
 * nowhere to carry it): per tab, gone when the tab closes, and good for NEXT_TTL_MS only, so
 * an abandoned sign-in cannot redirect a later, unrelated one. It is a strictly necessary
 * record of the sign-in the person asked for (listed as such in legal.inventory.ts).
 */
import { APP_ROLES, isAppRole } from './appRoles';

/** The query parameter /login takes the destination in. */
export const NEXT_PARAM = 'next';
/** `/login?sso=1` — the panel-entry path that always runs single sign-on (see Login.tsx). */
export const SSO_PARAM = 'sso';
/** The sessionStorage key holding the destination while the person is away at the IdP. */
export const NEXT_STORAGE_KEY = 'dijipo_next';
export const MAX_NEXT_LENGTH = 512;
/** As long as the backend's own state cookie (10 minutes): a slow sign-in still lands, an abandoned one dies. */
export const NEXT_TTL_MS = 10 * 60 * 1000;

const ROLE_PATH = new RegExp(`^/(${APP_ROLES.join('|')})(?:/|$)`);

function currentOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
}

/**
 * The normalised same-origin path (+ query) a destination refers to, or null when it is not an
 * acceptable one. With `role`, it must be one of THAT role's pages; without it, any app role's
 * (what /login can check before it knows who is signing in).
 */
export function safeNextPath(raw: unknown, options: { role?: string | null; origin?: string } = {}): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_NEXT_LENGTH) return null;
  // One slash, and then neither a second slash nor a backslash (protocol-relative, or the same thing to a browser).
  if (raw[0] !== '/' || raw[1] === '/' || raw[1] === '\\') return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f\\]/.test(raw)) return null;
  const origin = options.origin ?? currentOrigin();
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (url.pathname.includes('//')) return null;
  const match = ROLE_PATH.exec(url.pathname);
  if (!match) return null;
  if (options.role !== undefined && (!isAppRole(options.role) || match[1] !== options.role)) return null;
  return `${url.pathname}${url.search}`;
}

/**
 * The /login address that comes back to `pathname + search` after sign-in. Used where a
 * signed-out visitor is turned away from a page of `role` (DashboardLayout). A page that is
 * already the role's home carries no destination: there is nothing to come back to.
 */
export function loginPathFor(pathname: string, search: string, role: string): string {
  const next = safeNextPath(`${pathname}${search}`, { role });
  if (!next || next.replace(/\/$/, '') === `/${role}`) return '/login';
  return `/login?${NEXT_PARAM}=${encodeURIComponent(next)}`;
}

interface Stash {
  p: string;
  t: number;
}

/**
 * Remembers where the person was going, for the trip to the IdP and back. A destination that
 * fails the checks above is not stored (and an earlier one is cleared either way: this
 * sign-in is about THIS destination or none). Returns whether one was stored.
 */
export function stashNext(raw: unknown, now: number = Date.now()): boolean {
  clearStashedNext();
  const path = safeNextPath(raw);
  if (!path) return false;
  try {
    const stash: Stash = { p: path, t: now };
    sessionStorage.setItem(NEXT_STORAGE_KEY, JSON.stringify(stash));
    return true;
  } catch {
    return false; // storage off (private mode, quota): the person lands on their home, as before
  }
}

export function clearStashedNext(): void {
  try {
    sessionStorage.removeItem(NEXT_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * The destination remembered by stashNext, for the role that just signed in — or null. It is
 * consumed: it is gone after this call whatever the answer, so one sign-in lands once. It is
 * judged AGAIN here, against the real role and the clock: what was in storage is not trusted.
 */
export function takeStashedNext(role: string | null | undefined, now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(NEXT_STORAGE_KEY);
    sessionStorage.removeItem(NEXT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw || !role) return null;
  let stash: Partial<Stash>;
  try {
    stash = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!stash || typeof stash.p !== 'string' || typeof stash.t !== 'number') return null;
  if (now - stash.t > NEXT_TTL_MS || stash.t > now + 60_000) return null;
  return safeNextPath(stash.p, { role });
}

/**
 * The assignment id of a `?odev=<id>` deep link (student/Assignments.tsx), or null. Ids are
 * unsigned integers on the wire (strings); anything else is not an id and opens nothing.
 */
export function parseAssignmentId(raw: unknown): string | null {
  return typeof raw === 'string' && /^[1-9]\d{0,19}$/.test(raw) ? raw : null;
}
